/**
 * FE-4451 — assemble screen templates at render time.
 *
 * A component that carries a `template` field is a REFERENCE: its body lives in
 * the `templates` map delivered with the screen, keyed by external_id, and its
 * `overrides` hold whatever the screen's author changed. This turns that pair
 * back into an ordinary component tree before anything else looks at the page.
 *
 * MIRROR OF THE EDITOR, NOT SHARED CODE. `fractal-fe`'s
 * `features/screenTemplates/lib/expandTemplateRefs.ts` performs the same
 * assembly with the same id formula and the same merge rules; the two repos ship
 * separately, so this is a deliberate duplicate in the manner of
 * ROOT_FLEX_DEFAULTS. If the merge contract changes, BOTH move together or a
 * screen renders differently in the editor than it does in production.
 *
 * The one deliberate divergence is failure handling. The editor SHOWS an
 * unresolved reference so its author can fix it; this is a public screen, where
 * an unresolved reference is the renderer's normal silence — the component is
 * dropped, the rest of the page assembles, and diagnostics go to the console
 * and never into the markup.
 */

import {
  effectiveLocalsOf,
  layerTemplateVariables,
  qualifyLocalVariableRefs,
} from './templateLocalVariables';

type AnyRecord = Record<string, any>;

/**
 * Separator between the placed component's id and the template's own node id.
 *
 * Must match the editor's exactly: ids assembled here are the same ids the
 * editor persists into events, refs and form keys, so a divergence would break
 * every `object_id` reference the author wired up in the editor.
 */
export const TEMPLATE_NODE_SEPARATOR = '~';

const isPlainObject = (value: any): value is AnyRecord => (
  !!value && typeof value === 'object' && !Array.isArray(value)
);

/**
 * The body root of a map entry.
 *
 * Two envelopes, because both occur on the wire:
 *  - the entry is the RECORD (`{ id, name, status, data }`) — what
 *    `GET /screens/{key}?include=templates` returns;
 *  - the entry IS the body (`{ template: … }`) — what the public render
 *    envelope carries, where the metadata would be useless to a renderer.
 *
 * Reading only the record form is how the first version silently dropped every
 * reference on a real page: the key was present, the body was present, and the
 * lookup still answered "unresolved".
 *
 * The body is ALWAYS `data.template` — ONE root component (FE-4448), the only
 * form the backend reads (`data.template.style`). The `components` array this
 * used to also accept is gone: every consumer took `components[0]`, so a second
 * root was silently dropped at placement. The editor now refuses to save one.
 * Keep this in step with `fractal-fe`'s `templateRootOf`.
 */
const templateRootOf = (record: any): AnyRecord | null => {
  if (!isPlainObject(record)) return null;
  const source = isPlainObject(record.data) ? record.data : record;
  return isPlainObject(source.template) ? source.template : null;
};

/**
 * Drop `null`-valued entries at any depth.
 *
 * `null` means "fall back to the template value" — an erasure of an override,
 * not a value to write. Merging it verbatim would blank the template's own value.
 */
const withoutNulls = (value: any): any => {
  if (Array.isArray(value) || !isPlainObject(value)) return value;
  return Object.keys(value).reduce((acc: AnyRecord, key) => {
    if (value[key] === null) return acc;
    acc[key] = withoutNulls(value[key]);
    return acc;
  }, {});
};

/**
 * Deep-merge an override onto a template node.
 *
 * Same contract as `updates.replace`: `{style:{width}}` overrides `width` only.
 * Arrays are REPLACED, never index-merged — `background` and `options` are
 * collections whose element 0 bears no relation to the template's element 0.
 */
const mergeOverride = (node: AnyRecord, override: any): AnyRecord => {
  const clean = withoutNulls(override);
  if (!isPlainObject(clean)) return node;
  const out: AnyRecord = { ...node };
  Object.keys(clean).forEach((key) => {
    const value = clean[key];
    const current = out[key];
    out[key] = isPlainObject(value) && isPlainObject(current)
      ? mergeOverride(current, value)
      : value;
  });
  return out;
};

/** Rewrite event `object_id` cross-references through an old-id → new-id map. */
const remapEventTargets = (node: AnyRecord, idMap: Map<string, string>): AnyRecord => {
  const out: AnyRecord = { ...node };
  if (isPlainObject(out.events)) {
    out.events = Object.keys(out.events).reduce((acc: AnyRecord, eventType) => {
      const chunks = out.events[eventType];
      acc[eventType] = Array.isArray(chunks)
        ? chunks.map((chunk: any) => (
          isPlainObject(chunk) && typeof chunk.object_id === 'string' && idMap.has(chunk.object_id)
            ? { ...chunk, object_id: idMap.get(chunk.object_id) }
            : chunk
        ))
        : chunks;
      return acc;
    }, {});
  }
  if (Array.isArray(out.items)) {
    out.items = out.items.map((child: AnyRecord) => remapEventTargets(child, idMap));
  }
  return out;
};

/**
 * One node of a template body, re-keyed and merged with its override.
 *
 * The ROOT keeps the PLACED component's id — that is the id the screen and its
 * events already address — while descendants are namespaced by it, so the same
 * template placed twice produces two disjoint subtrees.
 */
const buildInstance = (
  body: AnyRecord,
  instanceId: string,
  overrides: AnyRecord,
  idMap: Map<string, string>,
  isRoot = true,
): AnyRecord => {
  const templateNodeId = body.id;
  const id = isRoot ? instanceId : `${instanceId}${TEMPLATE_NODE_SEPARATOR}${templateNodeId}`;
  idMap.set(templateNodeId, id);

  const children = Array.isArray(body.items) ? body.items : [];
  const items = children.map(
    (child: AnyRecord) => buildInstance(child, instanceId, overrides, idMap, false),
  );

  const merged = mergeOverride({ ...body }, overrides?.[templateNodeId]);
  return { ...merged, id, ...(items.length ? { items } : {}) };
};

/**
 * Expand every template reference in a section list.
 *
 * Returns a NEW array; nodes without a `template` field are passed through with
 * their children expanded, so a reference nested inside an ordinary container is
 * still assembled.
 *
 * `ancestry` guards against a template that references itself, directly or
 * through a chain, which would otherwise recurse until the stack blows.
 */
const containsTemplateRef = (nodes: any[]): boolean => nodes.some((node) => (
  isPlainObject(node)
  && ((typeof node.template === 'string' && !!node.template.trim())
    || (Array.isArray(node.items) && containsTemplateRef(node.items)))
));

/**
 * FE-4733 — how a reference in the list being expanded is named in the variable
 * path. On the screen it is the placement's own id; inside a body it is the
 * enclosing placement's path plus the TEMPLATE node id of the reference, because
 * each level stores its path relative to itself (the card declares
 * `action.state`, never `<card>~action.state`).
 */
type PlacementPathOf = (node: AnyRecord) => string;

const screenPlacementPath: PlacementPathOf = (node) => String(node.id);

const expandList = (
  sections: any,
  templates: AnyRecord,
  ancestry: string[],
  placementPathOf: PlacementPathOf,
): any[] => {
  const list = Array.isArray(sections) ? sections : [];
  // A screen with no references must come out BYTE-IDENTICAL — the same array,
  // the same node objects. The assembly below rebuilds every container it walks
  // through, and this runs on the bootstrap path for every screen ever served,
  // so without this scan a page that has nothing to do with templates would pay
  // a full tree rebuild and lose object identity on first paint.
  if (!containsTemplateRef(list)) return list;

  return list.reduce((acc: any[], node: any) => {
    if (!isPlainObject(node)) {
      acc.push(node);
      return acc;
    }

    const externalId = typeof node.template === 'string' ? node.template.trim() : '';
    if (!externalId) {
      acc.push(Array.isArray(node.items)
        ? { ...node, items: expandList(node.items, templates, ancestry, placementPathOf) }
        : node);
      return acc;
    }

    if (ancestry.indexOf(externalId) !== -1) {
      // eslint-disable-next-line no-console
      console.warn(`[templates] circular reference to ${externalId}; component "${node.id}" skipped`);
      return acc;
    }

    const body = templateRootOf(templates[externalId]);
    if (!body) {
      // UNRESOLVED — deleted permanently, or living in another branch. This is a
      // public screen: drop the component, assemble the rest, and say nothing in
      // the markup. The editor is the surface that shows failure states.
      // eslint-disable-next-line no-console
      console.warn(`[templates] unresolved template ${externalId} for component "${node.id}"`);
      return acc;
    }

    const idMap = new Map<string, string>();
    const instance = buildInstance(
      body,
      String(node.id),
      isPlainObject(node.overrides) ? node.overrides : {},
      idMap,
    );
    const remapped = remapEventTargets(instance, idMap);

    // FE-4733 — qualify this body's local references to the placement path, so
    // two placements of one template cannot share a value. It runs BEFORE the
    // nested expansion below: a nested reference is still an unexpanded
    // `{ template, overrides }` node here, so only names THIS level owns are
    // rewritten — its own declaration plus the names of its nested placements —
    // and a name the nested body uses without declaring it is left for the
    // screen (UC-7). Each nested body is then qualified with the longer path.
    const path = placementPathOf(node);
    const heads = Object.keys(effectiveLocalsOf(externalId, templates, templateRootOf, ancestry));
    const qualified = qualifyLocalVariableRefs(remapped, path, heads);
    const localIdOf = new Map<string, string>();
    idMap.forEach((assembledId, templateNodeId) => localIdOf.set(assembledId, templateNodeId));
    const nestedPathOf: PlacementPathOf = (nested) => (
      `${path}.${localIdOf.get(String(nested.id)) ?? String(nested.id)}`
    );

    acc.push({
      ...qualified,
      items: expandList(qualified.items, templates, ancestry.concat(externalId), nestedPathOf),
    });
    return acc;
  }, []);
};

export const expandTemplateRefs = (sections: any, templates: any): any[] => {
  const list = Array.isArray(sections) ? sections : [];
  if (!isPlainObject(templates)) return list;
  return expandList(list, templates, [], screenPlacementPath);
};

/**
 * FE-4733 — layer 1 of the variable merge: what every placement in `sections`
 * owns, keyed by the placement id. Walks the COLLAPSED sections, so it can run
 * beside `expandTemplateRefs` on the same input; each value is the bottom-up
 * walk of that placement's template (see `effectiveLocalsOf`).
 */
export const collectTemplateVariables = (sections: any, templates: any): AnyRecord => {
  const layer: AnyRecord = {};
  if (!isPlainObject(templates)) return layer;
  const visit = (nodes: any) => {
    (Array.isArray(nodes) ? nodes : []).forEach((node: any) => {
      if (!isPlainObject(node)) return;
      const externalId = typeof node.template === 'string' ? node.template.trim() : '';
      if (!externalId) {
        visit(node.items);
        return;
      }
      const locals = effectiveLocalsOf(externalId, templates, templateRootOf);
      if (Object.keys(locals).length) layer[String(node.id)] = locals;
    });
  };
  visit(sections);
  return layer;
};

/**
 * `settings` with the template layer laid UNDERNEATH its `variables` — a value
 * the screen saved beats the template's default. Returns `settings` itself when
 * no placement declares anything, so a screen without template locals
 * assembles exactly as before (UC-8).
 */
export const withTemplateVariables = (settings: any, sections: any, templates: any): any => {
  const layer = collectTemplateVariables(sections, templates);
  if (!Object.keys(layer).length) return settings;
  const base = isPlainObject(settings) ? settings : {};
  return { ...base, variables: layerTemplateVariables(layer, base.variables) };
};
